import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { AvGroup, AvInput } from 'availity-reactstrap-validation';
import { FormattedMessage, useIntl } from 'react-intl';
import { InputGroupAddon, Label } from 'reactstrap';
import {
  createLocaleNumberParser,
  isNumber,
} from '../../../../utility/util/util.service';
import { debounce } from 'debounce';

/**
 * The Price Field Component
 *
 * @param {Object} object - Used in combination with propName to get the internalValue from the object.
 * @param {string} propName - The name of the property in object. Also used to populate name, which is mandatory!
 * @param {number} value - The value (if you need to set it manually).
 * @param {string} labelMessageId - The message ID of the label for the input field.
 * @param {boolean} allowNullValue - Whether the field allows null values.
 * @param {boolean} allowNegativePrice - Whether the field allows negative prices.
 * @param {string} suffixSign - The sign of the currency to be displayed.
 * @param {function} onChange - The function to be called when the internalValue of the field changes (it is debounced!)
 * @param {...any} props - Additional props to be passed to the component.
 *
 * @returns {ReactElement} The rendered FormattedNumberField component.
 */
const FormattedNumberField = ({
  object,
  propName,
  value,
  labelMessageId,
  allowNullValue,
  allowNegativePrice,
  suffixSign,
  onChange,
  noLabel,
  groupClassName,
  useDefaultFormat = true,
  ...props
}) => {
  const intl = useIntl();
  const locale = intl.locale;

  const [internalValue, setInternalValue] = useState(() => {
    const initial = value || object?.[propName] || 0;
    return isNumber(initial)
      ? intl.formatNumber(
          initial,
          useDefaultFormat ? { format: 'priceDecimal' } : undefined,
        )
      : '';
  });

  const parseLocaleNumber = useMemo(() => createLocaleNumberParser(locale), [
    locale,
  ]);

  useEffect(() => {
    const givenValue = value || object?.[propName];
    if (isNumber(givenValue) || givenValue === null) {
      setInternalValue(
        isNumber(givenValue)
          ? intl.formatNumber(
              givenValue,
              useDefaultFormat ? { format: 'priceDecimal' } : undefined,
            )
          : '',
      );
    }
  }, [object, propName, value, intl]);

  const isValidPrice = (internalValue) => {
    return (
      (allowNullValue && internalValue === null) ||
      (isNumber(internalValue) && (allowNegativePrice || internalValue >= 0))
    );
  };

  const handleChange = useCallback(
    debounce((rawValue) => {
      if (rawValue === '') {
        setInternalValue(null);
        if (onChange) onChange(null);
      } else {
        const parsedNumber = parseLocaleNumber(rawValue);
        if (isValidPrice(parsedNumber)) {
          const formattedValue = intl.formatNumber(
            parsedNumber,
            useDefaultFormat
              ? {
                  format: 'priceDecimal',
                }
              : undefined,
          );
          setInternalValue(formattedValue);
          if (onChange) onChange(parsedNumber);
        }
      }
    }, 1000),
    [onChange, parseLocaleNumber, intl],
  );

  return (
    <AvGroup
      className={`form-label-group input-group input-group-merge ${groupClassName}`}
    >
      <AvInput
        key={propName}
        type="text"
        name={propName}
        placeholder={intl.formatMessage({ id: labelMessageId || propName })}
        value={internalValue === null ? '' : internalValue}
        validate={{ async: isValidPrice }}
        valueParser={(value) => {
          const parsedNumber = parseLocaleNumber(value);
          return isNumber(parsedNumber)
            ? parsedNumber
            : allowNullValue && value === ''
            ? null
            : Number.NaN;
        }}
        onChange={(e) => {
          e.persist();
          handleChange(e.target.value);
        }}
        {...props}
      />
      {!noLabel && (
        <Label for={object?.id}>
          <FormattedMessage id={labelMessageId || propName} />
        </Label>
      )}
      <InputGroupAddon addonType="append">{suffixSign}</InputGroupAddon>
    </AvGroup>
  );
};

export default FormattedNumberField;
